import { Component, computed, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { InteractiveItemComponent } from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';
import { extractXmlProps, upsertChildren, xmlElementToString } from '../../../../utils/xmlParser';
import { SignatureCanvasComponent } from './SignatureCanvas/signature-canvas.component';
import { DialogDrawerService } from '../../../shared/services/dialog-drawer.service';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';
import { MatIcon } from '@angular/material/icon';
import { hasValidGroupPermission } from '../../../../utils/hasValidGroupPermission';

interface SignatureItemProperties {
  text: string;
  groupItemIds: string[];
  groupItems: string[];
  signed: boolean;
  label: string;
  style: string;
}

const base64Header = 'data:image/png;base64,';

@Component({
  selector: 'lfe-signature-item-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SignatureCanvasComponent, MatIcon],
  templateUrl: './signature-item-form.component.html',
  standalone: true,
  styleUrl: './signature-item-form.component.css'
})
export class SignatureItemFormComponent
  extends InteractiveItemComponent<SignatureItemProperties>
  implements OnInit, OnDestroy
{
  private dialogDrawerService = inject(DialogDrawerService);
  @ViewChild('signatureCanvas', { static: false }) signatureCanvas!: SignatureCanvasComponent;

  translateService = inject(LiveFormsTranslateLoaderService);
  formControl: FormControl<string | null> | undefined;
  private valueChangeSubscription?: Subscription;

  properties: InteractiveItemProperties & SignatureItemProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    groupItemIds: [],
    groupItems: [],
    text: this.translateService.instant('liveforms-editor.signature-item.default-text'),
    signed: false,
    label: this.translateService.instant('liveforms-editor.signature-item.label'),
    style: 'Normal'
  };

  // Signals
  isOpen = signal(false);
  hasDrawn = signal(false);
  currentSignatureData = signal('');
  formControlValue = signal('');

  // Computed properties
  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `signature_${this.currentId()}`);

  initialValue = '';

  dataUrl = computed(() => {
    const value = this.formControlValue();
    return value ? `${base64Header}${value}` : '';
  });

  hasValue = computed(() => !!this.formControlValue());

  signatureInfo = computed(() => {
    const user = this.properties.resultUser || this.user().displayName;
    const dateOptions: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
    const date = this.properties.resultTime
      ? new Date(this.properties.resultTime).toLocaleString('de', dateOptions)
      : new Date().toLocaleString('de', dateOptions);
    return this.translateService.instant('liveforms-editor.signature-item.signature-info', { user, date });
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.formControlId = this.properties.customId || this.properties.id;
    this.initialValue = this.properties.resultValue || '';
    this.formControl = new FormControl(this.initialValue);
    this.form().addControl(this.formControlId, this.formControl);
    this.hasDrawn.set(false);

    // Initialize the signal with current value
    this.formControlValue.set(this.initialValue);

    // Subscribe to value changes
    this.valueChangeSubscription = this.formControl.valueChanges.subscribe((value) => {
      this.formControlValue.set(value || '');
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.valueChangeSubscription?.unsubscribe();
  }

  override extractLocalProperties(xml: Element): InteractiveItemProperties & SignatureItemProperties {
    const props = extractXmlProps(xml);

    const sGroupItemIds = props['GroupItemIDs'];
    const sGroupItems = props['GroupItems'];

    const groupItemIds: string[] = sGroupItemIds ? sGroupItemIds.split(';').map((id) => id.trim()) : [];
    const groupItems: string[] = sGroupItems
      ? sGroupItems.split(';').map((name) => name.trim())
      : [this.translateService.instant('liveforms-editor.common.default-group-name')];

    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id: props['ID'],
      groupItemIds,
      groupItems,
      customId: props['CustomID'],
      activationRule: props['ActivationRule'],
      resultValue: props['ResultValue'],
      resultUser: props['ResultUser'],
      resultTime: props['ResultTime'],
      text: props['Text'] || this.translateService.instant('liveforms-editor.signature-item.default-text'),
      signed: props['Signed'] === 'True',
      label: props['Label'] || this.translateService.instant('liveforms-editor.signature-item.label'),
      style: props['Style'] || 'Normal',
      isGenericRepeatItem
    };
  }

  override getXML(): string {
    const element = this.getXmlCloneElement();

    if (this.formControlValue() !== this.initialValue) {
      const signed = this.formControl?.value ? 'True' : 'False';

      upsertChildren(element, {
        Signed: signed
      });

      this.setResultChildren(element);
    }

    return xmlElementToString(element) || '';
  }

  onSignatureOpenClick(e: Event) {
    e.preventDefault();
    this.isOpen.set(true);
    this.hasDrawn.set(false);
  }

  onCanvasDrawn(hasDrawn: boolean) {
    this.hasDrawn.set(hasDrawn);
  }

  canSign = computed(() =>
    hasValidGroupPermission({
      properties: this.properties,
      defaultGroupName: this.translateService.instant('liveforms-editor.common.default-group-name'),
      userGroups: this.user().userGroups
    })
  );

  closeDialog() {
    const signatureData = this.signatureCanvas.getBase64();

    this.currentSignatureData.set(signatureData);

    this.properties.resultValue = signatureData;
    this.properties.resultUser = this.user().displayName || '';
    this.properties.resultTime = new Date().toISOString();

    const base64 = this.currentSignatureData();
    this.formControl?.setValue(base64);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();
    this.isOpen.set(false);
  }

  async resetSignature(): Promise<void> {
    const result = await this.dialogDrawerService.showUserPrompt({
      title: this.translateService.instant('liveforms-editor.signature-item.dialog.reset.title'),
      message: this.translateService.instant('liveforms-editor.signature-item.dialog.reset.message'),
      icon: 'info',
      iconColor: '#1976d2',
      actions: [
        {
          text: this.translateService.instant('liveforms-editor.signature-item.dialog.reset.actions.yes'),
          action: 'confirm',
          color: 'primary',
          variant: 'raised'
        },
        {
          text: this.translateService.instant('liveforms-editor.signature-item.dialog.reset.actions.no'),
          action: 'cancel',
          color: 'primary',
          variant: 'basic'
        },
        {
          text: this.translateService.instant('liveforms-editor.signature-item.dialog.reset.actions.cancel'),
          action: 'abort',
          color: 'primary',
          variant: 'basic'
        }
      ]
    });

    if (result === 'confirm') {
      this.formControl?.setValue('');
      this.formControl?.markAsDirty();
      this.formControl?.markAsTouched();
    }
  }
}
